import { useParams, Link } from 'react-router-dom';
import { categories, videoSites } from '@/mocks/videoData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

function RatingStars({ rating }: { rating: number }) {
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <i
          key={i}
          className={`text-[10px] ${
            i < full ? 'ri-star-fill text-secondary-500' : i === full && half ? 'ri-star-half-fill text-secondary-500' : 'ri-star-fill text-background-300'
          }`}
        ></i>
      ))}
    </div>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const sites = videoSites.filter((s) => s.category === slug);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${category.name} - 视频资源导航 | 福利视频导航`;
  const pageDesc = `${category.description}，福利视频导航为您精选${category.count}个优质${category.name}视频站点，持续更新中。`;

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: pageTitle,
            description: pageDesc,
            url: `https://fulishipin.com/category/${slug}`,
            isPartOf: { '@id': 'https://fulishipin.com', name: '福利视频导航' },
          }),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gradient-to-br from-primary-100 to-secondary-100">
                <i className={`${category.icon} text-xl text-primary-600`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">{category.name}</h1>
                <p className="text-sm text-foreground-500">{category.description}</p>
              </div>
            </div>
          </div>

          {/* Category tabs */}
          <div className="mb-8">
            <div className="flex items-center gap-1.5 flex-wrap p-1 bg-background-100 rounded-full w-fit">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50 font-medium'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {sites.map((site) => (
              <Link
                key={site.slug}
                to={`/article/${site.slug}`}
                className="group block bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-primary-300 hover:bg-background-50 transition-all duration-200"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 flex-shrink-0 rounded-lg bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center overflow-hidden">
                    <img
                      src={site.imageUrl}
                      alt={site.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                      {site.name}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-0.5">{site.visits} 访问</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <RatingStars rating={site.rating} />
                    <span className="text-xs text-primary-600 font-medium ml-1">{site.rating}</span>
                  </div>
                </div>
                <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">{site.description}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {site.tags.map((tag) => (
                    <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700">{tag}</span>
                  ))}
                </div>
              </Link>
            ))}
          </div>

          {sites.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无站点，敬请期待</p>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}